import { useCallback, useMemo, useState } from 'react';
import { useCopyActivationFlowConfig } from 'src/graphql';
import { useNotifications } from 'src/utils/hooks/use-notifications';

const COPY_FAILED_MESSAGE = 'Unable to copy configuration. Please try again.';

interface UseCopyConfigParams {
  partnerId: number | null;
  onCopied?: () => void | Promise<void>;
}

export const useCopyConfig = ({ partnerId, onCopied }: UseCopyConfigParams) => {
  const { showErrorAlert, showSuccessAlert } = useNotifications();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { copyActivationFlowConfig, loading: isCopying } = useCopyActivationFlowConfig();

  const closeModal = useCallback(() => setIsModalOpen(false), []);

  const handleCopyConfig = useCallback(
    async (sourceBrandId: string, targetBrandIds: string[]) => {
      if (!partnerId) return;

      try {
        const response = await copyActivationFlowConfig({
          variables: {
            input: { partnerId, sourceBrandId, targetBrandIds },
          },
        });

        if (!response.data?.copyActivationFlowConfig?.success) {
          showErrorAlert(COPY_FAILED_MESSAGE);
          return;
        }

        showSuccessAlert('Configuration copied successfully.');
        await onCopied?.();
      } catch (err) {
        console.error(err);
        showErrorAlert(COPY_FAILED_MESSAGE);
      }
    },
    [partnerId, copyActivationFlowConfig, showErrorAlert, showSuccessAlert, onCopied],
  );

  const configMenuOptions = useMemo(
    () => [
      {
        label: 'Copy configuration',
        testId: 'memberships-config-copy',
        icon: 'copy',
        iconLibrary: 'lucide' as const,
        onClick: () => setIsModalOpen(true),
      },
    ],
    [],
  );

  return { isModalOpen, closeModal, handleCopyConfig, isCopying, configMenuOptions };
};
